import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import comprasAnalisisService, {
  ComprasAnalisisDemandaHistorica,
  ComprasAnalisisItem,
} from '../../services/comprasAnalisisService';
import { getApiErrorMessage } from '../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  item: ComprasAnalisisItem | null;
}

const formatCantidad = (value: number, maxDecimals = 2): string =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(value);

const tendenciaConfig = (tendencia: string) => {
  switch (tendencia) {
    case 'Subiendo':
      return { color: 'success' as const, icon: <TrendingUpIcon fontSize="small" /> };
    case 'Bajando':
      return { color: 'error' as const, icon: <TrendingDownIcon fontSize="small" /> };
    default:
      return { color: 'default' as const, icon: <TrendingFlatIcon fontSize="small" /> };
  }
};

const MetricCard: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.25 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

const DemandaHistoricaDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ComprasAnalisisDemandaHistorica | null>(null);

  useEffect(() => {
    if (!open || !item?.codigoArticulo) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await comprasAnalisisService.getDemandaHistorica(item.codigoArticulo);
        if (!cancelled) setData(response);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(getApiErrorMessage(err, 'No se pudo cargar la evolución de demanda.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, item?.codigoArticulo]);

  const chartData = useMemo(
    () =>
      (data?.series ?? []).map((p) => ({
        ...p,
        cantidadChart: Math.max(0, p.cantidad),
      })),
    [data?.series],
  );

  const hayDemanda = chartData.some((p) => p.cantidad !== 0);
  const tendencia = tendenciaConfig(data?.tendencia ?? 'Estable');
  const um = data?.umCompra?.trim() || item?.umCompra?.trim() || 'UN';

  const coberturaLabel =
    data && data.coberturaDias >= 9999
      ? 'Sin consumo'
      : data
        ? `${formatCantidad(data.coberturaDias, 1)} días`
        : '-';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="demanda-historica-title"
    >
      <DialogTitle id="demanda-historica-title" sx={{ pr: 6, pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" fontWeight={700}>
            Evolución de demanda
          </Typography>
          {item && (
            <Typography variant="body2" color="text.secondary" noWrap title={`${item.codigoArticulo} — ${item.descripcion}`}>
              {item.codigoArticulo} — {item.descripcion}
            </Typography>
          )}
        </Stack>
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Calculando historial de ventas…
            </Typography>
          </Stack>
        )}

        {!loading && error && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'error.light' }}>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          </Paper>
        )}

        {!loading && !error && data && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                icon={tendencia.icon}
                label={`Tendencia: ${data.tendencia} (${data.variacionTendenciaPct > 0 ? '+' : ''}${formatCantidad(data.variacionTendenciaPct, 1)} % últ. 3 meses)`}
                color={tendencia.color}
                variant="outlined"
              />
              <Chip size="small" label={`UM: ${um}`} variant="outlined" />
              {item?.prioridad && (
                <Chip size="small" label={`Prioridad: ${item.prioridad}`} variant="outlined" />
              )}
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={4}>
                <MetricCard
                  label="Demanda mensual (6m)"
                  value={`${formatCantidad(data.demandaMensual6m)} ${um}`}
                  hint="Promedio usado en la sugerencia"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <MetricCard
                  label="Promedio 12 meses"
                  value={`${formatCantidad(data.demandaPromedio12m)} ${um}`}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <MetricCard
                  label="Total 12 meses"
                  value={`${formatCantidad(data.total12m)} ${um}`}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <MetricCard
                  label="Mes pico"
                  value={`${formatCantidad(data.mesMaximoCantidad)} ${um}`}
                  hint={data.mesMaximoEtiqueta}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <MetricCard
                  label="Mes mínimo"
                  value={`${formatCantidad(data.mesMinimoCantidad)} ${um}`}
                  hint={data.mesMinimoEtiqueta}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <MetricCard
                  label="Variabilidad (CV)"
                  value={`${formatCantidad(data.coeficienteVariacion, 1)} %`}
                  hint={`${data.mesesConDemanda} meses con ventas`}
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Cantidades vendidas — últimos 12 meses
              </Typography>
              {hayDemanda ? (
                <Box sx={{ width: '100%', height: isMobile ? 240 : 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: isMobile ? 28 : 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="etiqueta"
                        tick={{ fontSize: isMobile ? 10 : 11 }}
                        interval={isMobile ? 1 : 0}
                        angle={isMobile ? -35 : 0}
                        textAnchor={isMobile ? 'end' : 'middle'}
                        height={isMobile ? 48 : 32}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        width={isMobile ? 40 : 52}
                        tickFormatter={(v) => formatCantidad(Number(v), 0)}
                      />
                      <RechartsTooltip
                        formatter={(value: number, _name, props) => {
                          const payload = props?.payload as { cantidad?: number; esMesActual?: boolean } | undefined;
                          const real = payload?.cantidad ?? value;
                          const sufijo = payload?.esMesActual ? ' (mes en curso)' : '';
                          return [`${formatCantidad(real)} ${um}${sufijo}`, 'Demanda'];
                        }}
                        labelFormatter={(label) => `Mes: ${label}`}
                      />
                      <ReferenceLine
                        y={data.demandaPromedio12m}
                        stroke={theme.palette.warning.main}
                        strokeDasharray="4 4"
                        label={{
                          value: 'Prom. 12m',
                          position: 'insideTopRight',
                          fill: theme.palette.warning.dark,
                          fontSize: 11,
                        }}
                      />
                      <Bar dataKey="cantidadChart" name="Demanda" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry) => (
                          <Cell
                            key={`${entry.anio}-${entry.mes}`}
                            fill={entry.esMesActual ? theme.palette.primary.light : theme.palette.primary.main}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  Sin ventas registradas en los últimos 12 meses.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Facturas de venta (tipo 1) netas de notas de crédito (tipo 3). El mes actual puede estar incompleto.
              </Typography>
            </Paper>

            <Divider />

            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <MetricCard label="Stock neto" value={formatCantidad(data.stockNeto)} hint={um} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetricCard label="Cobertura" value={coberturaLabel} hint="Con demanda actual" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetricCard label="Lead time" value={`${formatCantidad(data.leadTimeDias, 0)} días`} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetricCard
                  label="Stock mín / máx"
                  value={`${formatCantidad(data.stockMin, 0)} / ${formatCantidad(data.stockMax, 0)}`}
                  hint={um}
                />
              </Grid>
            </Grid>

            {item?.consejo && (
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 2 }}>
                <InfoOutlinedIcon fontSize="small" color="primary" sx={{ mt: 0.25 }} />
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Interpretación sugerida
                  </Typography>
                  <Typography variant="body2">{item.consejo}</Typography>
                </Box>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }}>
          Compare la tendencia reciente con stock neto y lead time antes de confirmar la compra.
        </Typography>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemandaHistoricaDialog;
